import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { BrandLockup } from "../components/Brand";
import { ColorModeToggle } from "../components/ColorModeToggle";
import { confirmEmail, type ConfirmOutcome } from "../api/loginDetails";

/**
 * /confirm-email?t=… -- the link in a subscriber's "Confirm your email"
 * message (owner 2026-10-19; radius-wiki subscriber-cart-v1 §12.3). No sign-in:
 * the one-time token is the credential. Opening it confirms the address and,
 * when the link was sent for that, emails the subscriber's login details.
 */
export function ConfirmEmail() {
  const token = new URLSearchParams(useLocation().search).get("t") ?? "";
  const [outcome, setOutcome] = useState<ConfirmOutcome | null>(null);
  useEffect(() => {
    if (!token) return;
    const ac = new AbortController();
    void confirmEmail(token, ac.signal).then(setOutcome);
    return () => ac.abort();
  }, [token]);
  return (
    <Box sx={{ minHeight: "100dvh", bgcolor: "background.default" }}>
      <AppBar position="static" color="default" elevation={0}>
        <Toolbar>
          <BrandLockup to="/welcome" />
          <ColorModeToggle />
        </Toolbar>
      </AppBar>
      <Container maxWidth="sm" sx={{ py: { xs: 4, md: 6 } }}>
        <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
          Confirm your email
        </Typography>
        <ConfirmEmailView token={token} outcome={outcome} />
      </Container>
    </Box>
  );
}

/** The page's body without its hooks, so a test can render it. */
export function ConfirmEmailView({ token, outcome }: { token: string; outcome: ConfirmOutcome | null }) {
  if (!token || outcome?.kind === "expired") {
    return (
      <Alert severity="warning">
        <AlertTitle>This link has expired or was already used</AlertTitle>
        Ask your internet provider to send a new one.
      </Alert>
    );
  }
  if (!outcome) {
    return (
      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        <CircularProgress size={20} />
        <Typography variant="body2">Confirming…</Typography>
      </Box>
    );
  }
  if (outcome.kind === "unavailable") {
    return (
      <Alert severity="error">
        <AlertTitle>Not confirmed yet</AlertTitle>
        The service could not be reached. Open the link again in a few minutes.
      </Alert>
    );
  }
  return (
    <Alert severity="success">
      <AlertTitle>Your email is confirmed</AlertTitle>
      {outcome.loginSent ? "Your internet login details are on their way to this inbox. Keep the password private." : "Thank you. Nothing else is needed."}
    </Alert>
  );
}
